import { useCallback, useMemo, useState } from 'react';
import { EXPORT_FORMATS } from './exportConstants';
import { buildFilterSummary } from './exportConfigUtils';
import {
  disableDashboardExportMode,
  enableDashboardExportMode,
  prepareDomForExport,
  waitForExportCards,
} from './exportCaptureUtils';
import { generateDashboardImages } from './generateDashboardImages';
import { generateDashboardPdf } from './generateDashboardPdf';
import { useExportSelection } from './useExportSelection';

export const useLeadershipExport = ({
  exportConfig,
  filterValues,
  disabled = false,
  onBeforeExport,
  onAfterExport,
}) => {
  const [isExportDrawerOpen, setIsExportDrawerOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const {
    selectedItems,
    selectedCount,
    toggleItem,
    selectAll,
    clearAll,
    isSelected,
  } = useExportSelection(exportConfig);

  const filterSummary = useMemo(
    () => buildFilterSummary(exportConfig, filterValues),
    [exportConfig, filterValues]
  );

  const handleExport = useCallback(
    async (exportFormat) => {
      if (!selectedItems.length || isExporting || disabled) {
        return;
      }

      setIsExporting(true);
      enableDashboardExportMode();

      try {
        if (onBeforeExport) {
          await onBeforeExport(selectedItems);
        } else {
          await waitForExportCards(selectedItems);
        }

        await prepareDomForExport();

        if (exportFormat === EXPORT_FORMATS.PNG) {
          await generateDashboardImages({
            selectedItems,
            filePrefix: exportConfig.imageFilePrefix,
            zipFileName: exportConfig.imageZipFileName,
          });
        } else {
          const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
          await generateDashboardPdf({
            title: exportConfig.dashboardTitle,
            filterSummary,
            selectedItems,
            fileName: `${exportConfig.fileNamePrefix}-${timestamp}.pdf`,
          });
        }

        setIsExportDrawerOpen(false);
      } catch (_error) {
        // Keep drawer open so the user can retry.
      } finally {
        disableDashboardExportMode();
        if (onAfterExport) {
          await onAfterExport();
        }
        setIsExporting(false);
      }
    },
    [disabled, exportConfig, filterSummary, isExporting, onAfterExport, onBeforeExport, selectedItems]
  );

  return {
    isExportDrawerOpen,
    setIsExportDrawerOpen,
    isExporting,
    selectedItems,
    selectedCount,
    toggleItem,
    selectAll,
    clearAll,
    isSelected,
    filterSummary,
    handleExport,
  };
};
